import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "@/hooks/use-toast";
import { PhysicalOrdersService } from "@/services/physicalOrders.service";
import useModalStore from "@/store/useModalStore";
import { zodResolver } from "@hookform/resolvers/zod";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useEffect } from "react";
import { Controller, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { z } from "zod";
import { Order } from "../hooks/useOrders";
import { ShipmentSelector } from "./ShipmentSelector";

export const CANCEL_ORDER_MODAL_NAME = "cancel-order-modal";

const cancelOrderSchema = z.object({
  status: z.enum(["cancelled", "returned"]),
  return_address: z.string().max(1000, "max_length").optional(),
  tracking_id: z.string().optional(),
});

type CancelOrderFormData = z.infer<typeof cancelOrderSchema>;

interface CancelOrderModalProps {
  order: Order | null;
  defaultStatus?: "cancelled" | "returned";
  onSuccess?: () => void;
}

export function CancelOrderModal({ order, defaultStatus = "cancelled", onSuccess }: CancelOrderModalProps) {
  const { t } = useTranslation(["common", "orders"]);
  const { modals, closeModal } = useModalStore();
  const queryClient = useQueryClient();
  const isOpen = modals[CANCEL_ORDER_MODAL_NAME] || false;
  const requiresTrackingId = Boolean(order?.has_multiple_trackings);

  const {
    register,
    handleSubmit,
    control,
    reset,
    setValue,
    watch,
    formState: { errors, isValid },
  } = useForm<CancelOrderFormData>({
    resolver: zodResolver(cancelOrderSchema),
    mode: "onChange",
    defaultValues: {
      status: defaultStatus,
      return_address: "",
      tracking_id: "",
    },
  });

  const selectedStatus = watch("status");
  const trackingId = watch("tracking_id");

  useEffect(() => {
    if (isOpen) {
      reset({
        status: defaultStatus,
        return_address: "",
        tracking_id: "",
      });
    }
  }, [isOpen, reset, defaultStatus]);

  const { mutate: cancelOrder, isPending } = useMutation({
    mutationFn: async (data: CancelOrderFormData) => {
      if (!order) throw new Error("Order not found");
      if (requiresTrackingId && !data.tracking_id) {
        throw new Error(t("common:required_field"));
      }

      return PhysicalOrdersService.cancelOrder(order.id.replace("#", ""), {
        status: data.status,
        return_address: data.return_address || undefined,
        tracking_id: data.tracking_id || undefined,
      });
    },
    onSuccess: () => {
      const successMessage = selectedStatus === "returned"
        ? t("orders:cancel_order_modal.success_returned")
        : t("orders:cancel_order_modal.success_cancelled");

      toast({
        description: successMessage,
        variant: "success",
      });
      queryClient.invalidateQueries({ queryKey: ["physical-orders"] });
      queryClient.invalidateQueries({ queryKey: ["physical-orders-summary"] });
      queryClient.invalidateQueries({ queryKey: ["getSellInfo"] });
      onSuccess?.();
      handleClose();
    },
    onError: (error: any) => {
      toast({
        description: error?.response?.data?.message || t("common:data_submission_error"),
        variant: "destructive",
      });
    },
  });

  const handleClose = () => {
    reset({
      status: defaultStatus,
      return_address: "",
      tracking_id: "",
    });
    closeModal(CANCEL_ORDER_MODAL_NAME);
  };

  const onSubmit = (data: CancelOrderFormData) => {
    cancelOrder(data);
  };

  const canSubmit = isValid && (!requiresTrackingId || Boolean(trackingId));

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && handleClose()}>
      <DialogContent className="sm:max-w-[425px]">
        <DialogHeader>
          <DialogTitle>{t("orders:cancel_order_modal.title")}</DialogTitle>
          <DialogDescription>
            {t("orders:cancel_order_modal.description", { orderId: order?.id })}
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
          <div className="grid gap-4 py-4">
            <ShipmentSelector
              trackings={order?.delivery_trackings || []}
              value={trackingId}
              onChange={(value) => setValue("tracking_id", value, { shouldValidate: true })}
              error={requiresTrackingId && !trackingId}
            />
            {/* Tipo de status */}
            <div className="grid gap-2">
              <Label htmlFor="status">
                {t("orders:cancel_order_modal.status_type")}
                <span className="text-destructive ml-1">*</span>
              </Label>
              <Controller
                name="status"
                control={control}
                render={({ field }) => (
                  <Select value={field.value} onValueChange={field.onChange}>
                    <SelectTrigger id="status" className={errors.status ? "border-destructive" : ""}>
                      <SelectValue placeholder={t("orders:cancel_order_modal.select_status")} />
                    </SelectTrigger>
                    <SelectContent position="popper">
                      <SelectItem value="cancelled">
                        {t("orders:cancel_order_modal.status_cancelled")}
                      </SelectItem>
                      <SelectItem value="returned">
                        {t("orders:cancel_order_modal.status_returned")}
                      </SelectItem>
                    </SelectContent>
                  </Select>
                )}
              />
              {errors.status && (
                <span className="text-xs text-destructive">
                  {t("common:required_field")}
                </span>
              )}
            </div>

            {/* Endereço de retorno (aparece apenas para devolução) */}
            {selectedStatus === "returned" && (
              <div className="grid gap-2">
                <Label htmlFor="return_address">
                  {t("orders:cancel_order_modal.return_address")}
                </Label>
                <Textarea
                  id="return_address"
                  {...register("return_address")}
                  placeholder={t("orders:cancel_order_modal.return_address_placeholder")}
                  className={errors.return_address ? "border-destructive" : ""}
                  rows={3}
                />
                {errors.return_address && (
                  <span className="text-xs text-destructive">
                    {errors.return_address.message === "max_length"
                      ? t("orders:cancel_order_modal.max_length_error")
                      : t("common:required_field")}
                  </span>
                )}
                <span className="text-xs text-muted-foreground">
                  {t("orders:cancel_order_modal.return_address_hint")}
                </span>
              </div>
            )}
          </div>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={handleClose} disabled={isPending}>
              {t("common:cancel")}
            </Button>
            <Button type="submit" variant="destructive" disabled={!canSubmit || isPending}>
              {isPending ? t("common:saving") : (
                selectedStatus === "returned"
                  ? t("orders:cancel_order_modal.confirm_return")
                  : t("orders:cancel_order_modal.confirm_cancel")
              )}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
